import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import ProductCard from '../components/ProductCard';
import ProductImageGallery from '../components/ProductImageGallery';
import { useCart } from '../contexts/CartContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { getImageUrl } from '../utils/api';

const CatalogContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding-top: 80px;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  color: #333;
  padding: 60px 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid #dee2e6;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,107,53,0.05) 0%, transparent 70%);
    animation: float 6s ease-in-out infinite;
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(180deg); }
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  opacity: 0.95;
  line-height: 1.6;
  margin-bottom: 0;
  color: #666;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const MainContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px;
  
  @media (max-width: 768px) {
    padding: 20px 8px;
  }
`;

const FiltersSection = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 40px;
`;

const FiltersRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FiltersLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const CategoryFilter = styled.select`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const SearchInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  min-width: 250px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }

  &::placeholder {
    color: #999;
  }

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const SortSelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const ResultsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ResultsCount = styled.span`
  font-size: 16px;
  color: #666;
`;

const ActiveFilterBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #ff6b35;
  color: white;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  margin-left: 12px;
`;

const FilterRemoveBtn = styled.button`
  background: rgba(255, 255, 255, 0.3);
  border: none;
  color: white;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const LabelsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
`;

const LabelChip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #ff6b35;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e55a2b;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(255, 107, 53, 0.3);
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 10px;
`;

const ViewButton = styled.button`
  padding: 8px 12px;
  border: 2px solid #e1e5e9;
  background: ${props => props.$active ? '#ff6b35' : 'white'};
  color: ${props => props.$active ? 'white' : '#666'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$active ? '#e55a2b' : '#f8f9fa'};
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.viewMode === 'grid' ? 'repeat(auto-fill, minmax(300px, 1fr))' : '1fr'};
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: ${props => props.viewMode === 'grid' ? 'repeat(2, 1fr)' : '1fr'};
    gap: 10px;
  }
`;

const ProductListItem = styled.div`
  display: flex;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ProductImageContainer = styled.div`
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);

  @media (max-width: 768px) {
    width: 100%;
    height: 200px;
  }
`;

const ProductInfo = styled.div`
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ProductTop = styled.div`
  margin-bottom: 15px;
`;

const ProductBrand = styled.div`
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 5px;
`;

const ProductName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const ProductCode = styled.div`
  font-size: 12px;
  color: #999;
  margin-bottom: 10px;
`;

const ProductDescription = styled.p`
  color: #666;
  line-height: 1.5;
  margin-bottom: 15px;
`;

const ProductBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProductPrice = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #333;
`;

const ProductRating = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 10px;
`;

const Stars = styled.div`
  display: flex;
  gap: 2px;
`;

const Star = styled.span`
  color: ${props => props.$filled ? '#FFD700' : '#ddd'};
  font-size: 14px;
`;

const RatingText = styled.span`
  color: #666;
  font-size: 12px;
`;

const BuyButton = styled.button`
  background: #ff6b35;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4);
    background: #e55a2b;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #666;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 18px;
`;

const CategoryContentSection = styled.div`
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 40px;
  margin-bottom: 40px;
`;

const CategorySeoDescription = styled.div`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 30px;
  
  p {
    margin-bottom: 15px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const CategoryVideoWrapper = styled.div`
  margin-top: 30px;
  text-align: center;
`;

const CategoryVideoTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 10px;
  }
`;

const ITEMS_PER_PAGE = 12;

const Catalog = () => {
  const { categorySlug } = useParams();
  const [loadedProducts, setLoadedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [activeLabel, setActiveLabel] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const loadMoreRef = useRef(null);
  const { addToCart } = useCart();
  const { addToFavorites, removeFromFavorites } = useFavorites();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Проверяем URL параметры при загрузке и при изменении
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const labelParam = searchParams.get('label');
    if (labelParam) {
      const decodedLabel = decodeURIComponent(labelParam);
      if (activeLabel !== decodedLabel) {
        setActiveLabel(decodedLabel);
      }
    } else {
      if (activeLabel !== null) {
        setActiveLabel(null);
      }
    }
  }, [location.search, activeLabel]);

  // Функция преобразования товара из API в формат компонента
  const transformProduct = useCallback((product) => {
            const imageUrl = product.featured_image_url || getImageUrl(product.featured_image);
            
            // Обрабатываем массив изображений
            let images = [];
            if (product.images && Array.isArray(product.images)) {
              images = product.images.map(img => {
                if (typeof img === 'string') {
                  return img.startsWith('http') ? img : getImageUrl(img);
                }
                return img;
              });
            }
            
            // Обрабатываем КБЖУ
            let nutritionFacts = null;
            if (product.nutrition_facts) {
              try {
                if (typeof product.nutrition_facts === 'string') {
                  nutritionFacts = JSON.parse(product.nutrition_facts);
                } else {
                  nutritionFacts = product.nutrition_facts;
                }
              } catch (e) {
                console.error('Ошибка парсинга nutrition_facts:', e);
              }
            }
            
            // Обрабатываем метки
            let labels = [];
            if (product.labels) {
              try {
                if (typeof product.labels === 'string') {
                  labels = JSON.parse(product.labels);
                } else if (Array.isArray(product.labels)) {
                  labels = product.labels;
                }
              } catch (e) {
                console.error('Ошибка парсинга labels:', e);
              }
            }
            
            return {
            id: product.id,
            name: product.name,
            brand: product.brand || product.brand_name || 'Без бренда',
            price: parseFloat(product.price),
            oldPrice: product.compare_price ? parseFloat(product.compare_price) : null,
            code: product.sku || `P-${product.id}`,
            rating: (() => {
              // Обрабатываем NULL, undefined, пустую строку и 0
              const rawRating = product.avg_rating;
              let parsed = 0;
              if (rawRating !== null && rawRating !== undefined && rawRating !== '') {
                const numRating = parseFloat(rawRating);
                if (!isNaN(numRating) && numRating > 0) {
                  parsed = numRating;
                }
              }
              return parsed;
            })(),
            reviewsCount: product.reviews_count ? parseInt(product.reviews_count, 10) : 0,
            description: product.short_description || product.description || '',
      image: imageUrl || '/nofoto.png',
              featured_image: imageUrl || '/nofoto.png',
      images: images,
              category: product.category_slug || 'other',
            inStock: (product.total_stock_quantity || 0) > 0,
            total_stock_quantity: parseInt(product.total_stock_quantity || 0),
            isNew: product.created_at && new Date(product.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      created_at: product.created_at || null,
      points: product.points || 0,
      nutrition_facts: nutritionFacts,
      labels: labels,
    };
  }, []);

  // Функция загрузки товаров порциями
  const loadProducts = useCallback(async (offset = 0, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      
      // Формируем URL с параметрами фильтрации
      const searchParams = new URLSearchParams(location.search);
      const labelParam = searchParams.get('label');
      
      // Вычисляем page из offset (API использует page, а не offset)
      const page = Math.floor(offset / ITEMS_PER_PAGE) + 1;
      
      let apiUrl = `/api/products?page=${page}&limit=${ITEMS_PER_PAGE}`;
      if (labelParam) {
        apiUrl += `&label=${encodeURIComponent(labelParam)}`;
      }
      // Добавляем фильтр по категории
      if (category && category !== 'all') {
        apiUrl += `&category=${encodeURIComponent(category)}`;
      }
      
      // Добавляем параметры сортировки
      let sortByParam = 'created_at';
      let sortOrderParam = 'DESC';
      
      switch (sortBy) {
        case 'price-low':
          sortByParam = 'price';
          sortOrderParam = 'ASC';
          break;
        case 'price-high':
          sortByParam = 'price';
          sortOrderParam = 'DESC';
          break;
        case 'name':
          sortByParam = 'name';
          sortOrderParam = 'ASC';
          break;
        case 'newest':
          sortByParam = 'created_at';
          sortOrderParam = 'DESC';
          break;
        default:
          sortByParam = 'created_at';
          sortOrderParam = 'DESC';
      }
      
      apiUrl += `&sort_by=${sortByParam}&sort_order=${sortOrderParam}`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(apiUrl, {
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error('Ошибка загрузки товаров');
      }
      
      const data = await response.json();
      const productsList = data?.data?.products;
      const productsArray = Array.isArray(productsList) ? productsList : [];

      if (data.success && productsArray.length > 0) {
        // Преобразуем данные из PostgreSQL в формат, ожидаемый компонентом
        const products = productsArray.map(transformProduct);
        
        if (reset) {
          setLoadedProducts(products);
          setCurrentOffset(products.length);
          // Проверяем, есть ли еще товары для загрузки
          setHasMore(products.length === ITEMS_PER_PAGE && (data?.data?.total === undefined || products.length < (data?.data?.total || 0)));
        } else {
          setLoadedProducts(prev => {
            const newProducts = [...prev, ...products];
            setCurrentOffset(newProducts.length);
            // Проверяем, есть ли еще товары для загрузки
            setHasMore(products.length === ITEMS_PER_PAGE && (data?.data?.total === undefined || newProducts.length < (data?.data?.total || 0)));
            return newProducts;
          });
        }
      } else {
        if (reset) {
          setLoadedProducts([]);
          setCurrentOffset(0);
        }
        setHasMore(false);
        }
      } catch (error) {
        console.error('Ошибка при загрузке товаров:', error);
      if (reset) {
        setLoadedProducts([]);
        setCurrentOffset(0);
      }
      setHasMore(false);
      } finally {
      setLoading(false);
      setIsLoadingMore(false);
      }
  }, [location.search, sortBy, category, transformProduct]);

  // Загрузка категорий
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('/api/products/categories', {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            // Сортируем по sort_order
            const sortedCategories = data.data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            setCategories(sortedCategories);
            
            // Если есть categorySlug в URL, загружаем данные категории
            if (categorySlug) {
              const foundCategory = sortedCategories.find(cat => cat.slug === categorySlug);
              if (foundCategory) {
                setCurrentCategory(foundCategory);
                setCategory(foundCategory.slug);
              } else {
                // Загружаем категорию по slug с сервера
                fetch(`/api/products/categories/${categorySlug}`)
                  .then(res => res.json())
                  .then(result => {
                    if (result.success && result.data) {
                      setCurrentCategory(result.data);
                      setCategory(result.data.slug);
                    }
                  })
                  .catch(err => console.error('Ошибка загрузки категории:', err));
              }
            } else {
              // Если нет categorySlug, сбрасываем текущую категорию
              setCurrentCategory(null);
            }
          }
        }
      } catch (error) {
        console.error('Ошибка при загрузке категорий:', error);
      }
    };
    
    fetchCategories();
  }, [categorySlug]);

  // Первая загрузка товаров и сброс при изменении фильтров
  useEffect(() => {
    setCurrentOffset(0);
    setHasMore(true);
    loadProducts(0, true);
  }, [category, sortBy, activeLabel, location.search, loadProducts]);

  // Intersection Observer для бесконечной прокрутки
  useEffect(() => {
    if (!hasMore || isLoadingMore || loading) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry.isIntersecting && hasMore && !isLoadingMore && !loading) {
          loadProducts(currentOffset, false);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, isLoadingMore, loading, currentOffset, loadProducts]);

  // Используем useMemo для немедленного вычисления filteredProducts
  const filteredProducts = useMemo(() => {
    let filtered = [...loadedProducts]; // Создаем копию массива

    // Фильтрация по категории
    if (category !== 'all') {
      // Ищем категорию по slug
      const selectedCategory = categories.find(cat => cat.slug === category);
      if (selectedCategory) {
        filtered = filtered.filter(product => product.category === selectedCategory.slug);
      }
    }

    // Фильтрация по поиску (применённый запрос — после нажатия «Искать»)
    if (appliedSearchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(appliedSearchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(appliedSearchTerm.toLowerCase()) ||
        product.brand.toLowerCase().includes(appliedSearchTerm.toLowerCase())
      );
    }
    
    // Фильтрация по метке (если активна)
    // Примечание: фильтрация по метке уже происходит на сервере при загрузке,
    // но если activeLabel установлен, убеждаемся что товары соответствуют
    if (activeLabel) {
      filtered = filtered.filter(product => 
        product.labels && 
        Array.isArray(product.labels) && 
        product.labels.includes(activeLabel)
      );
    }

    // Сортировка с вторичными критериями
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => {
          const priceDiff = a.price - b.price;
          if (priceDiff !== 0) return priceDiff;
          // При одинаковой цене сортируем по названию
          return a.name.localeCompare(b.name, 'ru');
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const priceDiff = b.price - a.price;
          if (priceDiff !== 0) return priceDiff;
          // При одинаковой цене сортируем по названию
          return a.name.localeCompare(b.name, 'ru');
        });
        break;
      case 'rating':
        filtered.sort((a, b) => {
          const ratingA = a.rating || 0;
          const ratingB = b.rating || 0;
          // Товары с рейтингом 0 в конце
          if (ratingA === 0 && ratingB === 0) {
            // Оба без рейтинга - сортируем по количеству отзывов, затем по названию
            const reviewsDiff = (b.reviewsCount || 0) - (a.reviewsCount || 0);
            if (reviewsDiff !== 0) return reviewsDiff;
            return a.name.localeCompare(b.name, 'ru');
          }
          if (ratingA === 0) return 1; // a в конец
          if (ratingB === 0) return -1; // b в конец
          // Оба с рейтингом - сортируем по рейтингу
          const ratingDiff = ratingB - ratingA;
          if (ratingDiff !== 0) return ratingDiff;
          // При одинаковом рейтинге - по количеству отзывов
          const reviewsDiff = (b.reviewsCount || 0) - (a.reviewsCount || 0);
          if (reviewsDiff !== 0) return reviewsDiff;
          // При одинаковом количестве отзывов - по названию
          return a.name.localeCompare(b.name, 'ru');
        });
        break;
      case 'name':
        filtered.sort((a, b) => {
          const nameDiff = a.name.localeCompare(b.name, 'ru');
          if (nameDiff !== 0) return nameDiff;
          // При одинаковом названии сортируем по ID
          return a.id - b.id;
        });
        break;
      case 'newest':
        filtered.sort((a, b) => {
          // Новые товары сверху (более поздняя дата = выше)
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          // Товары без даты в конце
          if (dateA === 0 && dateB === 0) {
            return a.id - b.id; // При отсутствии даты сортируем по ID
          }
          if (dateA === 0) return 1; // a в конец
          if (dateB === 0) return -1; // b в конец
          // Сортируем по дате: более новые (больше timestamp) выше
          const dateDiff = dateB - dateA;
          if (dateDiff !== 0) return dateDiff;
          // При одинаковой дате - по ID
          return a.id - b.id;
        });
        break;
      default: // popular
        filtered.sort((a, b) => {
          const reviewsA = a.reviewsCount || 0;
          const reviewsB = b.reviewsCount || 0;
          // Товары без отзывов в конце
          if (reviewsA === 0 && reviewsB === 0) {
            // Оба без отзывов - сортируем по рейтингу, затем по названию
            const ratingDiff = (b.rating || 0) - (a.rating || 0);
            if (ratingDiff !== 0) return ratingDiff;
            return a.name.localeCompare(b.name, 'ru');
          }
          if (reviewsA === 0) return 1; // a в конец
          if (reviewsB === 0) return -1; // b в конец
          // Оба с отзывами - сортируем по количеству отзывов
          const reviewsDiff = reviewsB - reviewsA;
          if (reviewsDiff !== 0) return reviewsDiff;
          // При одинаковом количестве отзывов - по рейтингу
          const ratingDiff = (b.rating || 0) - (a.rating || 0);
          if (ratingDiff !== 0) return ratingDiff;
          // При одинаковом рейтинге - по названию
          return a.name.localeCompare(b.name, 'ru');
        });
    }
    return filtered;
  }, [loadedProducts, category, appliedSearchTerm, sortBy, categories, activeLabel]);
  
  const handleToggleFavorite = async (productId, isFavorite) => {
    try {
      if (isFavorite) {
        await removeFromFavorites(productId);
      } else {
        await addToFavorites(productId);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      if (error.message && error.message.includes('авторизация')) {
        alert('Необходима авторизация для добавления в избранное');
      } else {
        alert('Ошибка при изменении избранного');
      }
    }
  };

  const handleQuickView = (product) => {
    console.log('Быстрый просмотр:', product);
    // Здесь будет логика быстрого просмотра
  };

  const handleImageClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const renderStars = (rating) => {
    if (rating === 0) return <span style={{ color: '#999' }}>Нет отзывов</span>;
    return (
      <Stars>
        {Array.from({ length: 5 }, (_, index) => (
          <Star key={index} $filled={index < rating}>★</Star>
        ))}
      </Stars>
    );
  };

  const renderProductList = (product) => (
    <ProductListItem key={product.id}>
      <ProductImageContainer>
        <ProductImageGallery 
          images={product.images || []}
          featuredImage={product.image || product.featured_image}
          alt={product.name}
          onImageClick={() => handleImageClick(product.id)}
        />
      </ProductImageContainer>
      <ProductInfo>
        <ProductTop>
          <ProductBrand>{product.brand}</ProductBrand>
          <ProductName>{product.name}</ProductName>
          <ProductCode>#{product.code}</ProductCode>
          
          {/* Метки товара в списке */}
          {product.labels && Array.isArray(product.labels) && product.labels.length > 0 && (
            <LabelsContainer>
              {product.labels.slice(0, 3).map((label, index) => (
                      <LabelChip
                        key={index}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/catalog?label=${encodeURIComponent(label)}`);
                        }}
                      >
                        {label}
                      </LabelChip>
                    ))}
                    {product.labels.length > 3 && (
                      <LabelChip
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/catalog?label=${encodeURIComponent(product.labels[0])}`);
                        }}
                      >
                        +{product.labels.length - 3}
                      </LabelChip>
                    )}
            </LabelsContainer>
          )}
          
          <ProductRating>
            {renderStars(product.rating)}
            {product.rating > 0 && (
              <RatingText>({product.reviewsCount})</RatingText>
            )}
          </ProductRating>
          <ProductDescription>{product.description}</ProductDescription>
        </ProductTop>
        <ProductBottom>
          <ProductPrice>{product.price.toLocaleString()} ₽</ProductPrice>
          <BuyButton 
            onClick={() => {
              const stockQuantity = product.total_stock_quantity || 0;
              if (stockQuantity <= 0) {
                alert('Товар закончился, ожидайте');
                return;
              }
              addToCart(product);
            }}
            style={{ 
              opacity: (product.total_stock_quantity || 0) <= 0 ? 0.5 : 1,
              cursor: (product.total_stock_quantity || 0) <= 0 ? 'not-allowed' : 'pointer'
            }}
          >
            {(product.total_stock_quantity || 0) <= 0 ? 'Товар закончился' : 'Купить'}
          </BuyButton>
        </ProductBottom>
      </ProductInfo>
    </ProductListItem>
  );

  if (loading) {
    return (
      <CatalogContainer>
        <LoadingSpinner>Загрузка каталога...</LoadingSpinner>
      </CatalogContainer>
    );
  }

  return (
    <CatalogContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>{currentCategory ? (currentCategory.seo_title || currentCategory.name) : 'Каталог БАДов'}</HeroTitle>
          <HeroSubtitle>
            {currentCategory ? (currentCategory.description || 'Широкий выбор товаров в данной категории') : 'Широкий выбор биологически активных добавок для вашего здоровья и красоты'}
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <MainContent>
        <FiltersSection>
          <FiltersRow>
            <FiltersLeft>
              <CategoryFilter 
                value={category} 
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="all">Все категории</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.slug}>
                    {cat.name}
                  </option>
                ))}
              </CategoryFilter>
              
              <SearchInput
                type="text"
                placeholder="Поиск по названию или описанию..."
                value={searchInputValue}
                onChange={(e) => setSearchInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), setAppliedSearchTerm(searchInputValue))}
              />
              <button
                type="button"
                onClick={() => setAppliedSearchTerm(searchInputValue)}
                style={{
                  padding: '10px 18px',
                  marginLeft: '10px',
                  backgroundColor: '#ff6b35',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Искать
              </button>
            </FiltersLeft>
            
            <SortSelect 
              value={sortBy} 
              onChange={(e) => {
                const newSortBy = e.target.value;
                setSortBy(newSortBy);
              }}
            >
              <option value="newest">Новые сначала</option>
              <option value="popular">Сначала популярные</option>
              <option value="rating">С лучшим рейтингом</option>
              <option value="price-low">Сначала дешевые</option>
              <option value="price-high">Сначала дорогие</option>
              <option value="name">По алфавиту А-Я</option>
            </SortSelect>
          </FiltersRow>
        </FiltersSection>

        <ResultsInfo>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <ResultsCount>
              Найдено товаров: {filteredProducts.length}
            </ResultsCount>
            {activeLabel && (
              <ActiveFilterBadge>
                Метка: {activeLabel}
                <FilterRemoveBtn 
                  onClick={() => {
                    setActiveLabel(null);
                    navigate('/catalog');
                    window.location.reload();
                  }}
                  title="Убрать фильтр"
                >
                  ×
                </FilterRemoveBtn>
              </ActiveFilterBadge>
            )}
          </div>
          <ViewToggle>
            <ViewButton 
              $active={viewMode === 'grid'} 
              onClick={() => setViewMode('grid')}
            >
              Сетка
            </ViewButton>
            <ViewButton 
              $active={viewMode === 'list'} 
              onClick={() => setViewMode('list')}
            >
              Список
            </ViewButton>
          </ViewToggle>
        </ResultsInfo>

        {filteredProducts.length === 0 && !loading ? (
          <NoResults>
            По вашему запросу товары не найдены. Попробуйте изменить параметры поиска.
          </NoResults>
        ) : (
          <>
            <ProductGrid viewMode={viewMode}>
              {viewMode === 'grid' ? (
                filteredProducts.map(product => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onToggleFavorite={handleToggleFavorite}
                      onQuickView={handleQuickView}
                    />
                  ))
                ) : (
                filteredProducts.map(renderProductList)
              )}
            </ProductGrid>

            {/* Триггер для бесконечной прокрутки и индикатор загрузки */}
            {hasMore && (
              <div ref={loadMoreRef} style={{ height: '20px', marginTop: '20px' }} />
            )}
            
            {isLoadingMore && (
              <LoadingSpinner style={{ marginTop: '20px', marginBottom: '20px' }}>
                Загрузка товаров...
              </LoadingSpinner>
            )}
          </>
        )}
        
        {/* SEO контент и видео категории */}
        {currentCategory && (currentCategory.seo_description || currentCategory.rutube_video_url) && (
          <CategoryContentSection>
            {currentCategory.seo_description && (
              <CategorySeoDescription 
                dangerouslySetInnerHTML={{ __html: currentCategory.seo_description.replace(/\n/g, '<br/>') }}
              />
            )}
            {currentCategory.rutube_video_url && (
              <CategoryVideoWrapper>
                <CategoryVideoTitle>Видео о категории</CategoryVideoTitle>
                <VideoContainer>
                  {(() => {
                    // Извлекаем ID видео из URL Rutube
                    // Формат: https://rutube.ru/video/VIDEO_ID/ или https://rutube.ru/play/embed/VIDEO_ID
                    const videoIdMatch = currentCategory.rutube_video_url.match(/rutube\.ru\/(?:video|play\/embed)\/([a-zA-Z0-9]+)/);
                    const videoId = videoIdMatch ? videoIdMatch[1] : null;
                    
                    if (videoId) {
                      return (
                        <iframe
                          src={`https://rutube.ru/play/embed/${videoId}`}
                          allowFullScreen
                          title="Видео категории"
                        />
                      );
                    }
                    return <p>Неверный формат ссылки на видео Rutube</p>;
                  })()}
                </VideoContainer>
              </CategoryVideoWrapper>
            )}
          </CategoryContentSection>
        )}
      </MainContent>
    </CatalogContainer>
  );
};

export default Catalog; 